import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { combineLatest, NEVER, Observable, Subscription, BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { LocationRouteCffService } from '@fcom/core';
import { BoundType, CheapestBoundPrice, LocationInfoMap } from '@fcom/dapi/interfaces';
import { isIn, isNotEmpty, isPresent, isUndefined, unsubscribe } from '@fcom/core/utils';
import { NotificationTheme } from '@fcom/ui-components';
import { Profile } from '@fcom/core-api/login';
import { CmsTeaser } from '@fcom/core-api/interfaces';
import { FragmentService, ToasterService } from '@fcom/common/services';
import {
  Bound,
  Cabin,
  FareFamily,
  FinnairLocation,
  LocationWithDateTime,
  OfferList,
  FareInformation,
} from '@fcom/dapi/api/models';
import {
  isByBusOnly,
  isFlight,
  getAircraftTailForFlight,
  priorityServicesAvailable,
} from '@fcom/common-booking/utils/common-booking.utils';
import { finShare } from '@fcom/rx';
import { Direction } from '@fcom/common/interfaces/utils.interface';
import { SortBy } from '@fcom/common-booking';
import { LoaderType, OfferListFetchParams } from '@fcom/common/interfaces';
import {
  FlightSelectionFiltersComponent,
  ResetFiltersEvent,
} from '@fcom/common-booking/components/flight-selection-filters/flight-selection-filters.component';
import {
  getFareFamiliesLowestPriceOrPoints,
  mapLocationRouteCffDataToCabin,
  sortByPrice,
} from '@fcom/common/utils/booking.utils';
import { isShortHaulFlight } from '@fcom/common/utils/flight.utils';
import { FlightFilters, FlightTimeOption } from '@fcom/common-booking/interfaces/flight-filters.interface';
import { calculateLowesPrice } from '@fcom/common/utils/price-utils';
import {
  AirOffersStatus,
  BookingAppState,
  BoundInfoWithFareFamilies,
  BoundInfoWithFareFamiliesMap,
  SelectionPhase,
} from '@fcom/common/interfaces/booking';
import { PseudoLocalActions } from '@fcom/core/actions';
import { GLOBAL_BOOKING_WIDGET_EXPANDER_KEY } from '@fcom/booking-widget/constants';
import { ExpanderStatus } from '@fcom/booking-widget/interfaces';
import { pseudoLocal } from '@fcom/core/selectors';
import { effectiveCabinClass } from '@fcom/booking/modules/ticket-selection/utils/offer.utils';

import { OffersActions } from '../../../../store/actions';
import { AppConstants } from '../../../../app-constants';
import { BUS_AIRCRAFT_CODE } from '../../interfaces';
import { offerFilters, offerLastRequestParams } from '../../../../store/selectors/offers.selector';
import { offerListSortMap } from '../booking-ticket-selection-view.component';

export interface CabinClassFareFamily extends FareFamily {
  id: string;
  fareFamilyCode: string;
  fareInformation: FareInformation[];
  price: string;
  totalPrice: string;
  disabled?: boolean;
  totalPoints?: string;
}

export interface ExtendedBoundInfo extends Bound {
  disabled: boolean;
  selected: boolean;
  hasBusLeg: boolean;
  priorityServicesAvailable: boolean;
  cheapestCombinablePrice: string;
  fareFamilies?: CabinClassFareFamily[];
  departure: LocationWithDateTime & { cityName: string; name: string; countryCode: string };
  arrival: LocationWithDateTime & { cityName: string; name: string; countryCode: string };
}

@Component({
  selector: 'fin-bound-selection-list',
  styleUrls: ['./bound-selection-list.component.scss'],
  templateUrl: './bound-selection-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BoundSelectionListComponent implements OnInit, OnDestroy {
  readonly AirOffersStatus = AirOffersStatus;
  readonly C: typeof AppConstants = AppConstants;
  readonly LoaderType = LoaderType;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  currencyCode$: Observable<string>;
  @Input()
  boundType: BoundType;
  @Input()
  isLoading: boolean;
  @Input()
  selectionPhase: SelectionPhase;
  @Input()
  boundsStatus$: Observable<AirOffersStatus>;
  @Input()
  selectedSortBy$: Observable<SortBy>;
  @Input()
  bounds$: Observable<BoundInfoWithFareFamiliesMap>;
  @Input()
  cheapestCombinablePrices$: Observable<CheapestBoundPrice[]>;
  @Input()
  locations$: Observable<LocationInfoMap>;
  @Input()
  filteredOffers$: Observable<{ [key: string]: string[] }>;

  /** The sort order for the bound ids. */
  @Input()
  boundIdSortOrder$: Observable<string[]>;
  @Input()
  enabledBoundIds$: Observable<string[]>;
  @Input()
  selectedBound$: Observable<Bound>;
  @Input()
  selectedFareFamilyCode$: Observable<string>;
  @Input()
  isMobile$: Observable<boolean>;
  @Input()
  selectedOutbound$: Observable<Bound> = NEVER;
  @Input()
  profile$: Observable<Profile> = NEVER;
  @Input()
  showFlightSelectionFilters: boolean;

  @Output()
  openBound: EventEmitter<Bound> = new EventEmitter();
  @Output()
  selectBound: EventEmitter<ExtendedBoundInfo> = new EventEmitter();
  @Output()
  selectFareFamily: EventEmitter<string> = new EventEmitter();
  @Output()
  showDetails: EventEmitter<ExtendedBoundInfo> = new EventEmitter();
  @Output()
  scrollBenefits: EventEmitter<Direction> = new EventEmitter();
  @Output()
  selectSortBy: EventEmitter<SortBy> = new EventEmitter();
  @Output()
  showSpinner: EventEmitter<void> = new EventEmitter();

  @ViewChild(FlightSelectionFiltersComponent)
  private flightSelectionFilters: FlightSelectionFiltersComponent;

  boundsList$: Observable<ExtendedBoundInfo[]>;
  tails$: Observable<{ [boundId: string]: string[] }>;
  isBusOnlyConnections$: Observable<{ [boundId: string]: boolean }>;
  control: UntypedFormControl;
  selectedCabin$ = new BehaviorSubject<Cabin>(null);
  preSelectedFareFamilyId$ = new BehaviorSubject<string>(null);
  isShortHaul$: Observable<boolean>;
  layoverAmounts$: Observable<number[]>;
  originLocationCode$: Observable<string>;
  destinationLocationCode$: Observable<string>;
  fastestPrice$: Observable<number>;
  lowestPrice$: Observable<number>;
  enableFareFamilyAutoScroll$: Observable<boolean>;

  private subscription: Subscription = new Subscription();

  readonly BoundType = BoundType;
  availableCabinClasses$: Observable<Cabin[]>;
  filters$: Observable<FlightFilters>;
  lastRequestParam$: Observable<OfferListFetchParams>;
  isTravelClassDisabled$: Observable<boolean>;

  constructor(
    private toasterService: ToasterService,
    private fragmentService: FragmentService,
    private locationRouteCffService: LocationRouteCffService,
    private store$: Store<BookingAppState>
  ) {}

  ngOnInit(): void {
    this.control = new UntypedFormControl('');

    this.filters$ = this.store$.pipe(offerFilters());

    this.lastRequestParam$ = this.store$.pipe(offerLastRequestParams());
    this.isTravelClassDisabled$ = this.lastRequestParam$.pipe(
      map((params) => (params?.cabin === Cabin.MIXED ? false : true))
    );

    const selectedBoundId$: Observable<string> = this.selectedBound$.pipe(
      filter(isPresent),
      map(({ boundId }): string => boundId),
      distinctUntilChanged()
    );

    const sortedBounds$: Observable<BoundInfoWithFareFamilies[]> = combineLatest([
      this.bounds$.pipe(filter(isPresent)),
      this.boundIdSortOrder$.pipe(filter(isNotEmpty)),
    ]).pipe(
      map(([bounds, sortOrder]: [OfferList['outbounds'], string[]]): Bound[] =>
        sortOrder.map((id: string) => bounds[id]).filter(Boolean)
      ),
      filter((bounds) => bounds.every(Boolean)),
      finShare()
    );

    this.subscription.add(
      selectedBoundId$
        .pipe(
          withLatestFrom(sortedBounds$),
          map(([selected, bounds]: [string, Bound[]]): Bound => bounds.find(({ boundId }) => boundId === selected))
        )
        .subscribe((selectedBound) => this.control.setValue(selectedBound))
    );

    const cheapestCombinedPrice = (p: CheapestBoundPrice, b: Bound): string => p?.cheapestPrice || b.cheapestPrice;

    this.boundsList$ = combineLatest([
      sortedBounds$,
      this.enabledBoundIds$,
      this.cheapestCombinablePrices$,
      selectedBoundId$.pipe(startWith(undefined as string)),
      this.locations$,
      this.profile$,
      this.selectedFareFamilyCode$,
    ]).pipe(
      map(
        ([
          bounds,
          enabledBoundIds,
          cheapestPrices,
          selected,
          locations,
          profile,
          selectedFareFamilyCode,
        ]): ExtendedBoundInfo[] =>
          bounds
            .map((bound) => {
              return {
                ...bound,
                priorityServicesAvailable: priorityServicesAvailable(bound.itinerary),
                hasBusLeg: bound.itinerary.filter(isFlight).some(isByBusOnly),
                departure: {
                  ...bound.departure,
                  name: locations[bound.departure.locationCode]?.name,
                  cityName: locations[bound.departure.locationCode]?.cityName,
                  countryCode: locations[bound.departure.locationCode]?.countryCode,
                },
                arrival: {
                  ...bound.arrival,
                  name: locations[bound.arrival.locationCode]?.name,
                  cityName: locations[bound.arrival.locationCode]?.cityName,
                  countryCode: locations[bound.arrival.locationCode]?.countryCode,
                },
                disabled: !isIn(bound.boundId, ...enabledBoundIds),
                selected: bound.boundId === selected,
                cheapestCombinablePrice: cheapestCombinedPrice(
                  cheapestPrices.find((p) => p.boundId === bound.boundId),
                  bound
                ),
                fareFamilies: bound.fareFamilies.map((ff: CabinClassFareFamily) => ({
                  ...ff,
                  benefits: {
                    brandColor: ff.brandColor,
                    brandName: ff.brandName,
                    brandNameDescription: ff.brandNameDescription,
                    checkedBaggage: ff.checkedBaggage,
                    fareFamilyCode: ff.fareFamilyCode,
                    highlightedBenefitKeys: ff.highlightedBenefitKeys,
                    translatedBenefitRows: ff.translatedBenefitRows,
                  },
                  selected: ff.fareFamilyCode === selectedFareFamilyCode && bound.boundId === selected,
                  disabled: (profile && +profile.awardPoints < +ff.totalPoints) ?? false,
                })),
              };
            })
            .filter((bound) => bound.fareFamilies.length)
      ),
      finShare()
    );

    this.isShortHaul$ = this.boundsList$.pipe(
      map((bounds) =>
        isShortHaulFlight(
          { countryCode: bounds[0]?.departure.countryCode } as FinnairLocation,
          { countryCode: bounds[bounds.length - 1]?.arrival.countryCode } as FinnairLocation
        )
      )
    );

    this.layoverAmounts$ = this.boundsList$.pipe(map((bounds) => bounds.map((bound) => bound.stops)));

    this.originLocationCode$ = this.boundsList$.pipe(
      take(1),
      map((bounds) => bounds[0]?.departure.locationCode)
    );
    this.destinationLocationCode$ = this.boundsList$.pipe(
      take(1),
      map((bounds) => bounds[0]?.arrival.locationCode)
    );

    this.fastestPrice$ = combineLatest([
      this.bounds$.pipe(filter(isPresent)),
      this.filteredOffers$.pipe(filter(isPresent)),
    ]).pipe(
      filter(isPresent),
      map(([bounds, offers]: [BoundInfoWithFareFamiliesMap, { [key: string]: string[] }]) => {
        return offers[offerListSortMap[SortBy.DURATION]].reduce<number | null>((lowestPrice, sortingId) => {
          const fareFamilies = bounds[sortingId]?.fareFamilies;
          const lowestFFPrice = getFareFamiliesLowestPriceOrPoints(fareFamilies, true);

          return calculateLowesPrice(lowestPrice, lowestFFPrice);
        }, null);
      })
    );

    this.lowestPrice$ = combineLatest([
      this.bounds$.pipe(filter(isPresent)),
      this.filteredOffers$.pipe(filter(isPresent)),
    ])
      .pipe(filter(isPresent))
      .pipe(
        map(([bounds, offers]: [BoundInfoWithFareFamiliesMap, { [key: string]: string[] }]) => {
          const sortOrder = offers[offerListSortMap[SortBy.PRICE]];

          const results = sortOrder?.map((id: string) => bounds[id]).filter(Boolean);

          const sorted = sortByPrice(results);

          return sorted[0]?.fareFamilies
            ? getFareFamiliesLowestPriceOrPoints(sorted[0]?.fareFamilies, true)
            : undefined;
        }),
        finShare()
      );

    this.availableCabinClasses$ = combineLatest([this.originLocationCode$, this.destinationLocationCode$]).pipe(
      switchMap(([origin, destination]) => {
        return this.locationRouteCffService.routeCffsFor(origin, destination).pipe(
          take(1),
          map((result) => mapLocationRouteCffDataToCabin(result))
        );
      })
    );

    this.subscription.add(
      this.boundsList$
        .pipe(
          map((bounds) => bounds.some((bound) => bound.fareFamilies.some((ff) => ff.disabled))),
          filter(Boolean),
          switchMap(() => this.fragmentService.getFragment<CmsTeaser>('fragments.notEnoughPoints.url')),
          filter(Boolean),
          finShare()
        )
        .subscribe((fragment: CmsTeaser) => {
          this.toasterService.addMessageToQueue({
            id: 'AWARD_NOT_ENOUGH_POINTS_NOTIFICATION',
            content: fragment.teaserTitle,
            theme: NotificationTheme.WARNING,
            target: fragment.target
              ? {
                  linkUrl: fragment.target.url,
                  linkText: fragment.target.teaserTitle,
                  linkOpenInNewWindow: fragment.target.openInNewTab,
                }
              : undefined,
          });
        })
    );

    this.tails$ = this.boundsList$.pipe(
      map((boundsList) => {
        return boundsList.reduce((all, bound) => {
          all[bound.boundId] = bound.itinerary
            .filter(isFlight)
            .flatMap((itinerary) => getAircraftTailForFlight(itinerary));
          return all;
        }, {});
      })
    );

    this.isBusOnlyConnections$ = this.boundsList$.pipe(
      map((boundsList) => {
        return boundsList.reduce((all, bound) => {
          all[bound.boundId] = bound.itinerary
            .filter(isFlight)
            .flatMap((flight) => flight.aircraft?.code)
            .every((aircraftCode) => aircraftCode?.toUpperCase() === BUS_AIRCRAFT_CODE);
          return all;
        }, {});
      })
    );

    const isGlobalBookingWidgetClosed$ = this.store$.pipe(
      pseudoLocal(GLOBAL_BOOKING_WIDGET_EXPANDER_KEY),
      map(
        (status) => status === undefined || status === `${GLOBAL_BOOKING_WIDGET_EXPANDER_KEY}-${ExpanderStatus.CLOSE}`
      ),
      distinctUntilChanged()
    );

    this.enableFareFamilyAutoScroll$ = combineLatest([
      isGlobalBookingWidgetClosed$,
      this.preSelectedFareFamilyId$,
    ]).pipe(
      map(
        ([isGlobalBookingWidgetClosed, preSelectedFareFamilyId]) =>
          isGlobalBookingWidgetClosed && Boolean(preSelectedFareFamilyId)
      ),
      distinctUntilChanged(),
      finShare()
    );
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
  }

  handleCabinExpanding(bound: ExtendedBoundInfo, cabin: Cabin): void {
    if (!cabin || !bound) {
      return;
    }
    this.openBound.emit(bound);

    if (this.selectedCabin$.getValue() === null) {
      const ff = bound.fareFamilies.find((ff) => (effectiveCabinClass(ff) === cabin ? ff : undefined));

      if (effectiveCabinClass(ff) === cabin) {
        this.selectBoundAndFareFamily(bound, ff.fareFamilyCode);
        this.preSelectedFareFamilyId$.next(ff.id);
      }
      this.selectedCabin$.next(cabin);
    }

    this.store$.dispatch(
      PseudoLocalActions.setValue({
        key: GLOBAL_BOOKING_WIDGET_EXPANDER_KEY,
        data: `${GLOBAL_BOOKING_WIDGET_EXPANDER_KEY}-${ExpanderStatus.CLOSE}`,
      })
    );
  }

  cabinAutoScrolled(): void {
    this.preSelectedFareFamilyId$.next(null);
  }

  selectBoundAndFareFamily(bound: ExtendedBoundInfo, fareFamilyCode: string): void {
    this.selectBound.emit(bound);
    this.selectFareFamily.emit(fareFamilyCode);
  }

  onSelectSortBy(data: SortBy): void {
    this.selectSortBy.emit(data);
  }

  onSetFilter(flightFilters: FlightFilters): void {
    const filteredFilters = Object.entries(flightFilters)
      .filter(([_, value]) => !isUndefined(value))
      .reduce((obj, [filterName, value]) => ({ ...obj, [filterName]: value }), {});

    Object.entries(filteredFilters).forEach(([filterName, value]) => {
      switch (filterName as keyof FlightFilters) {
        case 'stopsCount':
          this.store$.dispatch(OffersActions.setStopsCount({ stopsCount: value as number | null }));
          break;

        case 'cabin':
          this.store$.dispatch(OffersActions.setCabin({ cabin: value as Cabin }));
          break;

        case 'departureTime':
          this.store$.dispatch(OffersActions.setDepartureTime({ departureTime: value as FlightTimeOption[] }));
          break;

        case 'arrivalTime':
          this.store$.dispatch(OffersActions.setArrivalTime({ arrivalTime: value as FlightTimeOption[] }));
          break;

        default:
          break;
      }
    });
  }

  onResetFilter(event: ResetFiltersEvent): void {
    switch (event.type) {
      case 'stopsCount':
        this.store$.dispatch(OffersActions.resetStopsCount());
        break;

      case 'cabin':
        this.store$.dispatch(OffersActions.resetCabin());
        break;

      case 'departureTime':
        this.store$.dispatch(OffersActions.resetDepartureTime());
        break;

      case 'arrivalTime':
        this.store$.dispatch(OffersActions.resetArrivalTime());
        break;

      default:
        break;
    }
  }

  onResetAllFilters(): void {
    this.store$.dispatch(
      OffersActions.resetAllFilters({ keepCabin: this.flightSelectionFilters.shouldNotClearTravelClass })
    );
  }

  clearAllFilters(): void {
    this.flightSelectionFilters.clearAllFilters('empty-state');
  }
}
